import { useCallback, useEffect, useRef, useState } from 'react';

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

// Loads dashboard data, keeps the last good snapshot visible while refreshing,
// and refreshes on an interval only while the tab is visible. A new fetcher
// (another branch) drops the previous snapshot so it is never shown as current.
export function useDashboardResource(fetcher, { refreshMs = 0, errorFallback = 'No se pudo cargar el tablero.' } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const requestId = useRef(0);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    const current = requestId.current + 1;
    requestId.current = current;
    inFlight.current = true;
    setLoading(true);
    try {
      const result = await fetcher();
      if (requestId.current !== current) return;
      setData(result);
      setError('');
      setUpdatedAt(new Date());
    } catch (err) {
      if (requestId.current !== current) return;
      setError(errorMessage(err, errorFallback));
    } finally {
      if (requestId.current === current) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  }, [fetcher, errorFallback]);

  useEffect(() => {
    setData(null);
    setUpdatedAt(null);
    setError('');
    void load();
  }, [load]);

  useEffect(() => {
    if (!refreshMs) return undefined;
    const timer = window.setInterval(() => {
      // A slow server must not pile up requests: skip the tick while one is pending.
      if (document.visibilityState === 'visible' && !inFlight.current) void load();
    }, refreshMs);
    return () => window.clearInterval(timer);
  }, [load, refreshMs]);

  useEffect(() => () => {
    requestId.current += 1;
  }, []);

  return { data, error, loading, updatedAt, reload: load };
}
